import Rails from "@rails/ujs"
import { createFocusTrap } from "focus-trap"

const questionMarkIcon = `
<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M12 6C10.957 6.00132 9.95716 6.41622 9.21968 7.1537C8.4822 7.89118 8.0673 8.89104 8.06598 9.934H10.066C10.066 8.867 10.934 8 12 8C13.066 8 13.934 8.867 13.934 9.934C13.934 10.532 13.453 10.966 12.718 11.56C12.4779 11.7481 12.2472 11.948 12.027 12.159C11.029 13.156 11 14.215 11 14.333V15H13L12.999 14.367C13 14.351 13.032 13.981 13.44 13.574C13.59 13.424 13.779 13.274 13.975 13.116C14.754 12.485 15.933 11.532 15.933 9.934C15.9322 8.89106 15.5176 7.89104 14.7802 7.15347C14.0428 6.4159 13.0429 6.00106 12 6ZM11 16H13V18H11V16Z" fill="currentColor"/>
<path d="M12 2C6.486 2 2 6.486 2 12C2 17.514 6.486 22 12 22C17.514 22 22 17.514 22 12C22 6.486 17.514 2 12 2ZM12 20C7.589 20 4 16.411 4 12C4 7.589 7.589 4 12 4C16.411 4 20 7.589 20 12C20 16.411 16.411 20 12 20Z" fill="currentColor"/>
</svg>
`

// Custom confirm modal to override Rails UJS default confirm
class CustomConfirm {
  constructor() {
    this.modalId = `vb-confirm-modal`
    this.focusTrap = null
    this.init()
  }

  init() {
    // Override Rails confirm method for fallback
    Rails.confirm = (message, element) => {
      return this.showModal(message, element)
    }

    // Intercept clicks BEFORE Rails UJS processes them
    document.addEventListener(
      `click`,
      (e) => {
        const element = e.target.closest(`[data-confirm]`)
        if (!element) return

        const confirmData = this.parseConfirmData(element)
        if (confirmData) {
          // Prevent the default action immediately
          e.preventDefault()
          e.stopImmediatePropagation()

          this.showCustomModal(confirmData, element).then((confirmed) => {
            if (confirmed) {
              this.executeOriginalAction(element)
            }
          })
        }
      },
      true
    ) // Use capture phase to intercept before Rails UJS
  }

  parseConfirmData(element) {
    const confirmAttr = element.dataset.confirm
    if (!confirmAttr) return null

    try {
      // Try to parse as JSON first (for custom options)
      return JSON.parse(confirmAttr)
    } catch (e) {
      // Fallback to simple string message
      return {
        title: `Är du säker?`,
        description: `Åtgärden går inte att ångra.`,
        btnText: `Ja`,
      }
    }
  }

  executeOriginalAction(element) {
    // Temporarily remove data-confirm to avoid infinite loop
    const originalConfirm = element.dataset.confirm
    delete element.dataset.confirm

    // Execute the original action based on element type
    if (element.tagName === `FORM`) {
      element.submit()
    } else if (element.tagName === `A`) {
      // For links, we need to handle different scenarios
      if (element.dataset.method) {
        // This is a Rails link with method (like delete)
        Rails.fire(element, `click`)
      } else {
        // Regular link
        window.location.href = element.href
      }
    } else {
      // For buttons or other elements, trigger click
      const clickEvent = new MouseEvent(`click`, {
        bubbles: true,
        cancelable: true,
        view: window,
      })
      element.dispatchEvent(clickEvent)
    }

    // Restore data-confirm after a short delay
    setTimeout(() => {
      element.dataset.confirm = originalConfirm
    }, 100)
  }

  showModal(message, element) {
    return new Promise((resolve) => {
      const modal = this.createModal({
        title: `Är du säker?`,
        description: message,
        btnText: `Ja`,
      })

      this.bindModalEvents(modal, resolve)
      document.body.appendChild(modal)
      this.showModalElement(modal)
      this.activateFocusTrap(modal)
    })
  }

  showCustomModal(options, element) {
    return new Promise((resolve) => {
      const modal = this.createModal(options)
      this.bindModalEvents(modal, resolve)
      document.body.appendChild(modal)
      this.showModalElement(modal)
      this.activateFocusTrap(modal)
    })
  }

  createModal(options) {
    const modal = document.createElement(`div`)
    modal.id = this.modalId
    modal.className = `vb-confirm-modal`
    modal.innerHTML = `
      <div class="vb-confirm-modal-backdrop"></div>
      <div class="vb-confirm-modal-content">
        <div class="vb-confirm-modal-header">
          <h3 class="-modal-title">${options.title}</h3>
          <p class="-modal-description">${options.description}</p>
        </div>
        <div class="vb-confirm-modal-footer">
          <button class="vb-confirm-modal-cancel btn btn--secondary">Avbryt</button>
          <button class="vb-confirm-modal-confirm btn btn--primary">${options.btnText}</button>
        </div>
      </div>
    `
    return modal
  }

  bindModalEvents(modal, resolve) {
    const confirmBtn = modal.querySelector(`.vb-confirm-modal-confirm`)
    const cancelBtn = modal.querySelector(`.vb-confirm-modal-cancel`)
    const backdrop = modal.querySelector(`.vb-confirm-modal-backdrop`)

    const handleConfirm = () => {
      this.hideModal(modal)
      resolve(true)
    }

    const handleCancel = () => {
      this.hideModal(modal)
      resolve(false)
    }

    confirmBtn.addEventListener(`click`, handleConfirm)
    cancelBtn.addEventListener(`click`, handleCancel)
    backdrop.addEventListener(`click`, handleCancel)

    // Handle ESC key
    const handleKeydown = (e) => {
      if (e.key === `Escape`) {
        handleCancel()
        document.removeEventListener(`keydown`, handleKeydown)
      }
    }
    document.addEventListener(`keydown`, handleKeydown)
  }

  activateFocusTrap(modal) {
    // Create focus trap for the modal content
    const modalContent = modal.querySelector(`.vb-confirm-modal-content`)

    this.focusTrap = createFocusTrap(modalContent, {
      initialFocus: false, // Don't auto-focus any element
      fallbackFocus: `.vb-confirm-modal-content`,
      escapeDeactivates: false, // We handle ESC manually
      returnFocusOnDeactivate: true,
      allowOutsideClick: true,
      clickOutsideDeactivates: false, // We handle backdrop clicks manually
      onActivate: () => {
        // Optional: Add any logic when focus trap activates
      },
      onDeactivate: () => {
        // Optional: Add any logic when focus trap deactivates
      },
    })

    // Activate the focus trap
    this.focusTrap.activate()
  }

  showModalElement(modal) {
    // Add show class for animations
    requestAnimationFrame(() => {
      modal.classList.add(`show`)
    })
  }

  hideModal(modal) {
    // Deactivate focus trap before hiding modal
    if (this.focusTrap) {
      this.focusTrap.deactivate()
      this.focusTrap = null
    }

    modal.classList.remove(`show`)
    setTimeout(() => {
      if (modal.parentNode) {
        modal.parentNode.removeChild(modal)
      }
    }, 300) // Match CSS transition duration
  }
}

// Initialize when DOM is ready
document.addEventListener(`DOMContentLoaded`, () => {
  new CustomConfirm()
})

export default CustomConfirm
